// ============================================================================
// JARVISTRAVEL - SIGN IN SCREEN
// Version: 3.0.0 — Clerk useSignIn() hook (JAR-392)
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import { taskRouter } from './sessionTask';
import { clerkErrorMessage, clerkFieldError } from '../../utils/clerkError';
import { usePasswordRules, checkPassword, describePasswordRules } from './passwordRules';
import { Mail, Lock, Eye, EyeOff, KeyRound } from 'lucide-react';
import { LogoLockup } from '@/app/components/shared/Logo';
import { Button, Divider, Input, OfflineNotice, toast } from '../shared';
import { APP_CONFIG } from '@/app/constants';
import { useForm, type FormValues } from '@/app/hooks';
import { validateEmail } from '@/app/utils';
import { DEV_TEST_EMAIL } from '@/app/utils/devEmail';
import { isSelfSignupEnabled } from '@/app/utils/earlyAccess';
import { describeIncompleteStatus, SIGN_IN_STILL_LOADING } from './signInStatus';
import { useSecondFactor } from './useSecondFactor';
import { SecondFactorPanel } from './SecondFactorPanel';
import { claimResume, resetAwaitingCode, resetAwaitingNewPassword, secondFactorCodeStillGood, signUpAwaitingCode } from './resumeAuth';
import { SignInUnreachable } from './SignInUnreachable';
import { useClerkStalled } from '@/app/hooks/useClerkStalled';

interface SignInScreenProps {
  onSignIn: (email: string, password: string, rememberMe: boolean) => void;
  onSignUp: () => void;
  onForgotPassword: () => void;
  /**
   * Where a sign-up still waiting on its emailed code is picked up (JAR-2492).
   * Optional for the same reason as onSessionTask: the routing is the app's.
   */
  onVerifyEmail?: () => void;
  onDemoLogin: () => void;
  /**
   * Where to send someone whose new session carries an outstanding Clerk task,
   * which on this instance means setting up a second factor. Optional because
   * the routing belongs to the app, not to this screen (JAR-1700).
   */
  onSessionTask?: (path: string) => void;
}

interface SignInFormFields {
  email: string;
  password: string;
  rememberMe: boolean;
}

type SignInFormData = FormValues<SignInFormFields>;

export function SignInScreen({ onSignIn, onSignUp, onForgotPassword, onVerifyEmail, onDemoLogin, onSessionTask }: SignInScreenProps) {
  const [showPassword, setShowPassword] = useState(false);
  const { isLoaded, signIn, setActive } = useSignIn();
  const { signUp } = useSignUp();
  const clerkStalled = useClerkStalled(isLoaded);
  // The new password set on a temporary one is judged by Clerk too (JAR-1702).
  const passwordRules = usePasswordRules();

  // The second factor (JAR-1391, JAR-1445) — shared with the reset screen since
  // JAR-1537, which is why its state and its one attemptSecondFactor call live in
  // useSecondFactor rather than here.
  const secondFactor = useSecondFactor({ onComplete: (email, remember) => onSignIn(email, '', remember) });

  // A reload or relaunch lands here, so this is where an interrupted flow is
  // picked up (JAR-2492): a second factor in place, a reset or a sign-up on its
  // own screen. Checked once, on the first render Clerk has loaded. Later status
  // changes are this screen's own submits, which handle themselves.
  const resumeChecked = useRef(false);
  useEffect(() => {
    if (!isLoaded || resumeChecked.current) return;
    resumeChecked.current = true;
    if (signIn?.status === 'needs_second_factor') {
      // Claimed even when the factor is refused: a later mount would only
      // refuse it, and say so, again.
      if (!claimResume()) return;
      const refusal = secondFactor.enter(
        signIn.supportedSecondFactors,
        signIn.identifier ?? '',
        false,
        secondFactorCodeStillGood(signIn),
      );
      if (refusal) toast.error(refusal);
    } else if (resetAwaitingCode(signIn) || resetAwaitingNewPassword(signIn)) {
      if (claimResume()) onForgotPassword();
    } else if (onVerifyEmail && signUpAwaitingCode(signUp)) {
      if (claimResume()) onVerifyEmail();
    }
  }, [isLoaded, signIn, signUp, secondFactor, onForgotPassword, onVerifyEmail]);

  // A dashboard-created user signs in with a password Clerk treats as
  // TEMPORARY, so signIn.create() resolves with `needs_new_password` rather
  // than throwing. The SignIn resource is already at the point where
  // resetPassword() is callable — the same call ForgotPasswordScreen makes
  // after its emailed code — so the new password is collected here.
  //
  // Deliberately NOT a hand-off to the reset flow. That flow would send a code
  // and make the user prove an identity they have just proved with a correct
  // password, and this instance has enhanced_email_deliverability off with no
  // custom sender, so it would trade a working path for one that depends on
  // email arriving. Nothing here touches email at all.
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetErrors, setResetErrors] = useState<Record<string, string>>({});
  const [isResetting, setIsResetting] = useState(false);
  const [rememberOnReset, setRememberOnReset] = useState(false);
  const [emailOnReset, setEmailOnReset] = useState('');

  const handleSetNewPassword = async () => {
    const errs: Record<string, string> = {};
    if (!newPassword) errs.newPassword = 'Password is required';
    else {
      const broken = checkPassword(newPassword, passwordRules);
      if (broken) errs.newPassword = broken;
    }
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (newPassword !== confirmPassword) errs.confirmPassword = 'Passwords do not match';
    if (Object.keys(errs).length > 0) {
      setResetErrors(errs);
      return;
    }

    setIsResetting(true);
    setResetErrors({});
    try {
      if (!isLoaded) return;
      const result = await signIn.resetPassword({ password: newPassword });
      if (result.status === 'needs_second_factor') {
        // A temporary password on an account with a second factor (JAR-1445). The
        // new password is set, and the factor is the rest of this sign-in, on the
        // panel the sign-in path uses. It used to fall into the branch below and
        // show the raw status, with no way to finish.
        const refusal = secondFactor.enter(result.supportedSecondFactors, emailOnReset, rememberOnReset);
        // The password has already changed here, so the refusal says so. Under the
        // password field without it, it would read as a rejected password.
        if (refusal) setResetErrors({ confirmPassword: `Your new password is set. ${refusal}` });
        return;
      }
      if (result.status !== 'complete') {
        // Same rule as the sign-in path: name the status rather than inventing
        // a reason. A second non-complete status here is not an inbox problem.
        setResetErrors({ confirmPassword: describeIncompleteStatus(result.status) });
        return;
      }
      const task = taskRouter(onSessionTask);
      await setActive({
        session: result.createdSessionId,
        // A user with no second factor yet gets a PENDING session carrying
        // Clerk's setup-mfa task, which only its own screen finishes (JAR-1700).
        navigate: task.navigate,
      });
      // Not when a task was routed. onSignIn navigates unconditionally, so it
      // would land on top of the redirect Clerk just asked for (see taskRouter).
      if (task.routed) return;
      onSignIn(emailOnReset, newPassword, rememberOnReset);
    } catch (err) {
      // A refused password belongs under the password, not under its confirmation
      // (JAR-1702). Everything else keeps its old place.
      const passwordRefusal = clerkFieldError(err, 'password');
      if (passwordRefusal) {
        setResetErrors({ newPassword: passwordRefusal });
        return;
      }
      setResetErrors({
        confirmPassword: clerkErrorMessage(
          err,
          'Couldn\'t set your password.',
          'Couldn\'t set your password. Please try again.',
        ),
      });
    } finally {
      setIsResetting(false);
    }
  };

  // One way back to the sign-in form from either interrupted flow (web-app#305
  // review). Without it, the wrong account or an expired Clerk attempt meant a
  // page reload. The Clerk attempt is simply abandoned: the next submit starts a
  // fresh one with signIn.create().
  const backToSignIn = () => {
    secondFactor.reset();
    setNeedsNewPassword(false);
    setNewPassword('');
    setConfirmPassword('');
    setResetErrors({});
  };

  const { values, errors, touched, isSubmitting, handleChange, handleBlur, handleSubmit } = useForm<SignInFormData>({
    initialValues: { email: DEV_TEST_EMAIL, password: '', rememberMe: false },
    validate: (vals) => {
      const errs: Partial<Record<keyof SignInFormData, string>> = {};
      if (!vals.email) errs.email = 'Email is required';
      else if (!validateEmail(vals.email)) errs.email = 'Invalid email address';
      if (!vals.password) errs.password = 'Password is required';
      return errs;
    },
    onSubmit: async (vals) => {
      // Before Clerk has loaded there's nothing to send the details to. Say so,
      // rather than a button that does nothing (JAR-2493).
      if (!isLoaded) {
        toast.error(SIGN_IN_STILL_LOADING);
        return;
      }
      try {
        const result = await signIn.create({
          identifier: vals.email,
          password: vals.password,
        });

        if (result.status === 'needs_new_password') {
          // The password was CORRECT. Clerk is saying it was set by the
          // dashboard or the Backend API and must be replaced on first use.
          setEmailOnReset(vals.email);
          setRememberOnReset(vals.rememberMe);
          setNeedsNewPassword(true);
          return;
        }

        if (result.status === 'needs_second_factor') {
          // The password was CORRECT and the account has MFA. Not an error —
          // the next step of a successful sign-in.
          //
          // But only if it is a factor this screen can complete. The first
          // version of this branch assumed TOTP for every account, so an account
          // whose second factor is a phone or email code was asked for an
          // authenticator code that was never sent — a DISHONEST dead end in
          // place of the honest one this ticket removed (web-app#305 review, bug
          // · medium). So: read what Clerk says this attempt supports.
          const refusal = secondFactor.enter(result.supportedSecondFactors, vals.email, vals.rememberMe);
          if (refusal) toast.error(refusal);
          return;
        }

        if (result.status !== 'complete') {
          // Logged as well as shown: the status is the one fact that makes a
          // support report actionable, and it is not in the toast a user
          // screenshots.
          console.error('[SignInScreen] sign-in did not complete', { status: result.status });
          toast.error(describeIncompleteStatus(result.status));
          return;
        }

        // Activate the Clerk session
        const task = taskRouter(onSessionTask);
        await setActive({
          session: result.createdSessionId,
          // A user with no second factor yet gets a PENDING session carrying
          // Clerk's setup-mfa task, which only its own screen finishes (JAR-1700).
          navigate: task.navigate,
        });

        // Clerk handles the session — parent picks up the user via useUser().
        // Notify parent for analytics + navigation.
        //
        // Not when a task was routed. onSignIn navigates unconditionally, so it
        // would land on top of the redirect Clerk just asked for (see taskRouter).
        if (task.routed) return;
        onSignIn(vals.email, vals.password, vals.rememberMe);
      } catch (err) {
        toast.error(
          clerkErrorMessage(err, 'Sign in failed', 'Couldn\'t sign in. Check your details and try again.'),
        );
      }
    },
  });

  return (
    <div className="min-h-[100dvh] bg-bg flex flex-col">
      {/* Header with logo */}
      <div className="pt-12 pb-8 px-6 text-center">
        {/* The real mark, not a lucide glyph in a tile (JAR-886). This is the
            first screen a new user sees, and it used to disagree with the
            LoaderScreen rendered immediately before it, which already draws
            LogoLockup.

            No tile: containment is option B — the swift stands free in clear
            space, never boxed. Colour follows the surface via currentColor,
            which is why the class sets a text colour rather than a fill.

            Still an <h1>, with the mark inside it. Replacing the heading with
            a bare <svg> would have taken the page's only top-level heading
            away from screen-reader users to gain a picture; keeping the title
            on the mark gives the heading its accessible name, announced
            once. */}
        <h1 className="mb-1">
          <LogoLockup
            className="h-10 w-auto mx-auto text-ateneo dark:text-moonlight"
            title={APP_CONFIG.name}
          />
        </h1>
        <p className="text-muted mt-1">Welcome back</p>
      </div>

      {/* The second factor — the `needs_second_factor` branch (JAR-1391).
          Rendered INSTEAD of the sign-in form, for the same reason the
          new-password panel is: the credentials are already accepted, and
          re-offering them invites a second attempt that lands right here. */}
      {secondFactor.active && <SecondFactorPanel secondFactor={secondFactor} onBack={backToSignIn} />}

      {/* Set a new password — the `needs_new_password` branch (JAR-1352).
          Rendered INSTEAD of the sign-in form: the credentials are already
          accepted and re-offering them would invite a second attempt that
          lands in exactly the same place. */}
      {!secondFactor.active && (needsNewPassword ? (
        <div className="flex-1 px-6 pb-8">
          <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto bg-accent/10 rounded-2xl flex items-center justify-center text-accent mb-3">
                <KeyRound className="w-7 h-7" strokeWidth={1.75} />
              </div>
              <h2 className="text-lg font-semibold text-body">Choose a new password</h2>
              {/* Says why, because "set a new password" straight after a
                  SUCCESSFUL sign-in reads as an error otherwise. */}
              <p className="text-muted text-sm mt-1">
                Your account was set up with a temporary password. Pick your own to finish signing in.
              </p>
            </div>

            <form
              className="space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSetNewPassword();
              }}
            >
              {/* Present and hidden, so a password manager attributes the new
                  password to the right account. Without it the manager has no
                  username on this form and offers to save an orphan. */}
              <input type="hidden" name="username" autoComplete="username" value={emailOnReset} readOnly />

              <Input
                type={showPassword ? 'text' : 'password'}
                label="New password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                error={resetErrors.newPassword}
                helperText={describePasswordRules(passwordRules)}
                leftIcon={<Lock className="w-5 h-5" strokeWidth={1.75} />}
                autoComplete="new-password"
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="text-muted hover:text-body"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.75} /> : <Eye className="w-5 h-5" strokeWidth={1.75} />}
                  </button>
                }
              />

              <Input
                type={showPassword ? 'text' : 'password'}
                label="Confirm new password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={resetErrors.confirmPassword}
                leftIcon={<Lock className="w-5 h-5" strokeWidth={1.75} />}
                autoComplete="new-password"
              />

              <OfflineNotice />

              <Button type="submit" variant="primary" fullWidth loading={isResetting} size="lg">
                Set password and sign in
              </Button>

              {/* The same trap the 2FA panel had, pre-dating it (web-app#305
                  review): no way back without a reload. */}
              <button
                type="button"
                className="w-full text-sm text-muted hover:text-body transition-colors"
                onClick={backToSignIn}
                // Disabled while Clerk is still answering (web-app#305 review). Backing
                // out only resets this screen; the request in flight would still land,
                // and could sign the user in after they chose a different account.
                disabled={isResetting}
              >
                Use a different account
              </button>
            </form>
          </div>
        </div>
      ) : (

      /* Sign in form */
      <div className="flex-1 px-6 pb-8">
        <div className="bg-surface border border-border rounded-3xl shadow-xl shadow-black/5 p-6 max-w-md mx-auto">
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              type="email"
              label="Email"
              placeholder="your@email.com"
              value={values.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              error={touched.email ? errors.email : undefined}
              leftIcon={<Mail className="w-5 h-5" strokeWidth={1.75} />}
              autoComplete="email"
            />

            <Input
              type={showPassword ? 'text' : 'password'}
              label="Password"
              placeholder="••••••••"
              value={values.password}
              onChange={(e) => handleChange('password', e.target.value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? errors.password : undefined}
              leftIcon={<Lock className="w-5 h-5" strokeWidth={1.75} />}
              rightIcon={
                <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="text-muted hover:text-body">
                  {showPassword ? <EyeOff className="w-5 h-5" strokeWidth={1.75} /> : <Eye className="w-5 h-5" strokeWidth={1.75} />}
                </button>
              }
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={values.rememberMe}
                  onChange={(e) => handleChange('rememberMe', e.target.checked)}
                  className="w-4 h-4 accent-brand rounded"
                />
                <span className="text-sm text-body">Remember me</span>
              </label>
              <button type="button" onClick={onForgotPassword} className="text-sm text-accent font-medium hover:opacity-80">
                Forgot password?
              </button>
            </div>

            {/* Inline while unreachable — submit stays enabled; a submit IS the
                retry (JAR-314). The app-wide banner handles SR announcement. */}
            <OfflineNotice />
            {clerkStalled && <SignInUnreachable />}

            <Button type="submit" variant="primary" fullWidth loading={isSubmitting} size="lg">Sign In</Button>
          </form>

          {/* No social sign-in row until a provider is enabled in Clerk and wired to
              its OAuth flow (JAR-1406). The buttons had no handler, and the instance
              has no social connections, so all four did nothing. */}

          {/* New to JarvisTravel: a prominent outlined Start Planning button under
              Sign In, so sign-up reads as the clear second action. Sign In stays the
              only filled button on the screen (JAR-3095, Brent's approved design). */}
          <div className="mt-6">
            {/* The shared primitive, not a hand-rolled copy of it: the same
                markup already exists in components/shared/Divider.tsx and is
                tracked as a design-library consumer component (JAR-3095 review). */}
            <Divider text="New to JarvisTravel?" />
            {/* While Early Access is on (JAR-34) the destination is the
                invite-only notice, so the label promises access rather than
                instant signup. */}
            <Button type="button" variant="outline" fullWidth size="lg" onClick={onSignUp} className="mt-4">
              {isSelfSignupEnabled() ? 'Start Planning' : 'Request access'}
            </Button>
          </div>
        </div>

        {/* A look before an account: the demo is the line the sign-up link held.
            "Try the demo account" is the accent link, calling onDemoLogin (JAR-3095). */}
        <p className="text-center mt-6 text-muted">
          Want a look first?{' '}
          <button onClick={onDemoLogin} className="text-accent font-semibold hover:opacity-80">
            Try the demo account
          </button>
        </p>
      </div>
      ))}
    </div>
  );
}

export default SignInScreen;
